/**
 * heicDecode.js — HEIC detection + lazy-loaded decoder.
 *
 * FT-342 (2026-07-08) — capture pipeline.
 * See: Maritime-Agents/seaready-app/FT-340-photos/DESIGN-BRIEF.md §4.4.
 *
 * Two responsibilities:
 *   1. `isHeic(file)` — magic-byte sniff on the first 12 bytes. DO NOT trust
 *      `file.type` because iOS Safari sometimes labels HEIC as `image/jpeg`.
 *   2. `heicDecode(blob)` — dynamic-import `heic2any` so the ~1MB WASM cost
 *      is code-split into a lazy chunk that non-HEIC users never download.
 */

// The 6 known ftyp brand codes that indicate an HEIC/HEIF container.
// heic + heix: HEVC-encoded HEIF (standard iPhone photo)
// hevc:        HEVC image sequence
// heim:        HEVC image mixed
// mif1:        HEIF image with multiple items
// msf1:        HEIF image sequence
// Regex matches the marker at bytes 4..12 of the file: `ftyp<brand>`.
const HEIC_FTYP_RE = /^ftyp(heic|heix|hevc|heim|mif1|msf1)/;

/**
 * Detect HEIC by magic bytes. Reads only the first 12 bytes.
 * @param {File|Blob} file
 * @returns {Promise<boolean>}
 */
export async function isHeic(file) {
  if (!file || typeof file.slice !== 'function') return false;
  const slice = file.slice(0, 12);
  const buf = typeof slice.arrayBuffer === 'function'
    ? await slice.arrayBuffer()
    : await readAsArrayBuffer(slice);
  const bytes = new Uint8Array(buf);
  if (bytes.length < 12) return false;
  // TextDecoder is universally available in modern browsers + jsdom.
  const marker = new TextDecoder().decode(bytes.slice(4, 12));
  return HEIC_FTYP_RE.test(marker);
}

// Portable Blob read path — jsdom's older Blob shim doesn't expose arrayBuffer.
function readAsArrayBuffer(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Decode a HEIC blob to a JPEG Blob at Q0.9. Lazy-imports `heic2any` so it is
 * bundle-split; non-HEIC users pay nothing.
 * @param {Blob} blob
 * @returns {Promise<Blob>} JPEG blob
 */
export async function heicDecode(blob) {
  if (!isBlobLike(blob)) {
    throw new Error('[heicDecode] expected Blob input');
  }
  // Dynamic import: webpack code-splits this into a lazy chunk.
  const mod = await import('heic2any');
  const heic2any = mod.default || mod;
  const result = await heic2any({ blob, toType: 'image/jpeg', quality: 0.9 });
  // heic2any can return Blob or Blob[] (for multi-image containers). Take first.
  const jpeg = Array.isArray(result) ? result[0] : result;
  if (!isBlobLike(jpeg)) {
    throw new Error('[heicDecode] heic2any returned unexpected type');
  }
  return jpeg;
}

// Duck-type Blob check — `instanceof Blob` is unreliable across jsdom worker
// boundaries. Real Blobs have `size`, `type`, `slice`. Good enough.
function isBlobLike(v) {
  return v && typeof v === 'object' && typeof v.size === 'number' && typeof v.type === 'string' && typeof v.slice === 'function';
}

export default heicDecode;
